import { useState } from "react";

import {
  Accordion,
  type AccordionItem,
  AccordionVariant,
} from "@vega-core/components/Accordion/Accordion";
import { DropdownButton } from "@vega-core/components/Buttons/DropdownButton/DropdownButton";
import { InteractiveButton } from "@vega-core/components/Buttons/InteractiveButton";
import { type NestedOptionType } from "@vega-core/components/Form/Controls/Dropdown/NestedMultiselectDropdown/types";
import { Checkbox } from "@vega-core/components/shadcn/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@vega-core/components/shadcn/popover";
import { m } from "@vega-core/paraglide/messages";

export type MultiSelectCatFilterProps = {
  categories?: NestedOptionType[];
  selectedCategories: NestedOptionType[];
  setSelectedCategories: (subOptions: NestedOptionType[]) => void;
  buttonLabel: string;
  name?: string;
  disabled?: boolean;
};

export function MultiSelectCatFilter({
  categories,
  selectedCategories,
  setSelectedCategories,
  buttonLabel,
  name,
  disabled = false,
}: MultiSelectCatFilterProps) {
  const [open, setOpen] = useState(false);

  const handleSelectAll = (category: NestedOptionType) => {
    const selectedCategory = [...selectedCategories].find(
      (cat) => cat.value === category.value,
    );
    const isAllCategorySelected = category.subOptions?.every((subItem) =>
      selectedCategory?.subOptions?.some(
        (selectedSubItem) => selectedSubItem.value === subItem.value,
      ),
    );

    let newSelectedCategories = [...selectedCategories];

    if (isAllCategorySelected) {
      // Deselect all subOptions in category
      newSelectedCategories = newSelectedCategories.filter(
        (cat) => cat.value !== category.value,
      );
    } else {
      const hasSomesubOptionsSelected = category.subOptions?.some((subItem) =>
        selectedCategory?.subOptions?.some(
          (selectedSubItem) => selectedSubItem.value === subItem.value,
        ),
      );

      if (hasSomesubOptionsSelected) {
        newSelectedCategories.map((cat) =>
          cat.value === category.value ? category : cat,
        );
      } else {
        newSelectedCategories.push(category);
      }
    }
    setSelectedCategories(newSelectedCategories);
  };

  const handlesubOptionselect = (item: NestedOptionType) => {
    let newSelectedCategories = [...selectedCategories];
    const categoryValue = item.value;
    const existingCategory = newSelectedCategories.find(
      (cat) => cat.value === categoryValue,
    );

    if (existingCategory) {
      // Check if the specific item is already selected
      const isItemAlreadySelected = existingCategory.subOptions?.some(
        (selectedItem) => {
          const selectedValue = selectedItem.value;
          const itemValue = item.subOptions?.[0].value;

          return selectedValue === itemValue;
        },
      );

      if (isItemAlreadySelected) {
        // Remove the specific item
        const updatedsubOptions = existingCategory.subOptions?.filter(
          (selectedItem) => {
            const selectedValue = selectedItem.value;
            const itemValue = item.subOptions?.[0].value;

            return selectedValue !== itemValue;
          },
        );

        if (updatedsubOptions?.length === 0) {
          // If no subOptions left, remove the entire category
          newSelectedCategories = newSelectedCategories.filter(
            (cat) => cat.value !== categoryValue,
          );
        } else {
          // Update with remaining subOptions
          existingCategory.subOptions = updatedsubOptions;
        }
      } else {
        // Add the item to existing category
        existingCategory.subOptions?.push(item.subOptions![0]);
      }
    } else {
      // Category doesn't exist, add it with the selected item
      newSelectedCategories.push({
        value: categoryValue,
        label: item.label,
        subOptions: [item.subOptions![0]],
      });
    }

    console.log(
      "New selected categories after handlesubOptionselect:",
      newSelectedCategories,
    );
    setSelectedCategories(newSelectedCategories);
  };

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <div>
          <DropdownButton
            dataTestId={
              name
                ? `${name}-multi-select-filter-button`
                : "multi-select-filter-button"
            }
            disabled={disabled || !categories?.length}
            onClick={() => {
              setOpen(!open);
            }}
            open={open}
          >
            <div className="flex subOptions-center justify-between w-full">
              {buttonLabel}
              {selectedCategories.length > 0 && (
                <span className="inline-flex subOptions-center justify-center h-4.5 min-w-4.5 px-1 py-0.5 ml-2 bg-text/interactive rounded-small">
                  <p className="body-small-semibold text-background/primary">
                    {selectedCategories.reduce(
                      (acc, category) => acc + category.subOptions!.length,
                      0,
                    )}
                  </p>
                </span>
              )}
            </div>
          </DropdownButton>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="min-w-[200px] p-0"
        data-testid="multi-select-filter-popover"
      >
        {categories && categories.length > 0 ? (
          <Accordion
            items={
              categories
                .toSorted((a, b) => a.label.localeCompare(b.label))
                .map((category: NestedOptionType) => {
                  const categorysubOptions = category.subOptions ?? [];
                  const categorysubOptionsLength = categorysubOptions.length;

                  if (!categorysubOptionsLength) return null;

                  return {
                    title: category.label,
                    content: (
                      <>
                        {categorysubOptionsLength > 1 && (
                          <InteractiveButton
                            className="underline py-2"
                            dataTestId={`select-all-${category.value}-button`}
                            onClick={() => handleSelectAll(category)}
                          >
                            {m.input_select_all()}
                          </InteractiveButton>
                        )}
                        {categorysubOptions
                          .toSorted((a, b) => a.label.localeCompare(b.label))
                          .map((item) => {
                            const issubOptionselected =
                              selectedCategories
                                .find((cat) => cat.value === category.value)
                                ?.subOptions?.some(
                                  (subItem) => subItem.value === item.value,
                                ) || false;

                            const itemAsNestedOption: NestedOptionType = {
                              value: category.value,
                              label: category.label,
                              subOptions: [item],
                            };

                            return (
                              <div
                                className="flex gap-2 subOptions-center py-3 cursor-pointer body-base-medium"
                                key={item.value}
                                onClick={() =>
                                  handlesubOptionselect(itemAsNestedOption)
                                }
                              >
                                <Checkbox
                                  checked={issubOptionselected}
                                  onCheckedChange={() =>
                                    handlesubOptionselect(itemAsNestedOption)
                                  }
                                />
                                {item.label}
                              </div>
                            );
                          })}
                      </>
                    ),
                    value: category.value,
                  };
                })
                .filter((item) => item !== null) as AccordionItem[]
            }
            type="single"
            variant={AccordionVariant.SELECT_CATEGORIES}
          />
        ) : (
          <div className="p-4 text-center text-text/secondary">
            {m.general_no_options_available()}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
